import {Button, IconButton, Menu, MenuItem} from "@mui/material";
import ChevronIcon from "assets/icons/ChevronIcon";
import CrossIcon from "assets/icons/CrossIcon";
import BasicTextInput from "components/BasicInputs/BasicTextInput";
import ListSubItem from "components/Table2/CellComps/ListSubItem";
import {CSSProperties, ReactNode, useEffect, useRef, useState} from "react";

export interface Option {
    label: string;
    value: string | number;
    image?: string;
    dsc?: string;
    ind?: string | number;
    title?: string;
}

export interface BasicSelectProps {
    onChange?: (val: any, option?: any) => void;
    onClear?: () => void;
    options: Option[];
    defaultValue?: string | number | null;
    placeholder?: string;
    label?: string;
    labelKey?: string;
    valKey?: string;
    style?: CSSProperties;
    btnStyle?: CSSProperties;
    menuItemStyle?: CSSProperties;
    isNested?: boolean;
    required?: boolean;
    withSearch?: boolean;
    withClear?: boolean;
    closeOnFooterClick?: boolean;
    footer?: ReactNode;
    actionSection?: ReactNode;
    disabled?: boolean;
    preferences?: {
        size?: 1 | 2 | 3 | 4;
    };
}

export default function SettingsBasicSelect(props: BasicSelectProps) {
    /* ---------------------------------- Props --------------------------------- */
    const {onChange, defaultValue, label, style, required, onClear, labelKey, valKey} = props;
    const {btnStyle, menuItemStyle, disabled, withSearch, withClear, footer, actionSection} = props;
    const {preferences} = props;

    /* ---------------------------------- Hooks --------------------------------- */
    const btnRef = useRef<any>(null);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [value, setValue] = useState(defaultValue);
    const [options, setOptions] = useState(props.options);
    const [isNested, setNested] = useState(props.isNested);
    const [placeholder, setPlaceholder] = useState<string | number>();

    /* -------------------------------- Variables ------------------------------- */
    const open = Boolean(anchorEl);
    const id = open ? "simple-popper" : undefined;
    const flatOptions = rec(props.options, "childs", []);
    const size = preferences?.size || 3;
    const fontSizes = {1: 10, 2: 12, 3: 14, 4: 16, 5: 18};
    const hights = {1: 28, 2: 32, 3: 36, 4: 54, 5: 70};

    /* --------------------------------- Effects -------------------------------- */
    useEffect(() => setValue(defaultValue), [defaultValue]);
    useEffect(() => setOptions(props.options), [props.options]);

    useEffect(() => {
        const opts = !isNested ? options : flatOptions;
        const option: any = getOption(value, opts);
        let key = labelKey || (isNested ? "title" : "label");
        option && option[key] && setPlaceholder(option[key]);
    }, [value]);

    /* -------------------------------- Functions ------------------------------- */
    const handleChange = (option: any) => {
        const val = option.value || option.id;
        setAnchorEl(null);
        !isNested && setValue(val);
        let key = labelKey || (isNested ? "title" : "label");
        option && option[key] && setPlaceholder(option[key]);
        onChange && onChange(val, option);
        setNested(props.isNested);
    };

    function getOption(v: BasicSelectProps["defaultValue"], o: BasicSelectProps["options"]) {
        let id = 0;
        if (v && typeof v === "string") id = parseInt(v);
        if (v && typeof v === "number") id = v;
        const key = valKey || "value";
        const option =
            o &&
            Array.isArray(o) &&
            o.find((x) => {
                const y = (x as any)[key];
                return y && (y === id || y === v);
            });
        return option;
    }

    function search(text: string) {
        let data = props.isNested ? flatOptions : props.options;
        return data.filter((x: any) => (x.label as string).toLowerCase().includes(text.toLowerCase()));
    }

    function rec(data: any, key: string, arr: any[]) {
        if (data && data[key] && data[key].length === 0)
            arr.push({
                title: data.title,
                id: data.id,
                label: data.label || data.title,
                value: data.value || data.id,
            });
        if (data && data[key] && data[key].length > 0) {
            for (const item of data[key]) {
                rec(item, key, arr);
            }
        }
        return arr;
    }

    /* ----------------------------------- JSX ---------------------------------- */
    return (
        <div style={{...(style || {})}}>
            {label && (
                <div
                    style={{
                        fontSize: 14,
                        fontWeight: 400,
                        height: 32,
                        padding: "0px 8px 12px 0px",
                        color: "#33383D",
                    }}
                >
          <span>
            {label}
              {required && "*"}
          </span>
                </div>
            )}
            <div className="flex items-center gap-2">
                <Button
                    aria-describedby={id}
                    ref={btnRef}
                    style={{
                        width: "100%",
                        minHeight: 40,
                        minWidth: 150,
                        border: "1px solid #C2D0E3",
                        borderRadius: "6px",
                        justifyContent: "space-between",
                        backgroundColor: "transparent",
                        padding: "0px 6px",
                        fontSize: "14px",
                        gap: 12,
                        ...btnStyle,
                    }}
                    onClick={(e) => !disabled && setAnchorEl(e.currentTarget)}
                    color="inherit"
                    variant="outlined"
                >
          <span
              style={{
                  color: placeholder ? "#3D3D3D" : "#747575",
                  fontSize: fontSizes[size],
                  lineHeight: 1.2,
                  overflow: "hidden",
                  whiteSpace: "nowrap",
                  textOverflow: "ellipsis",
                  textTransform: "none",
              }}
          >
            {placeholder || props.placeholder}
          </span>
                    <span>
            {withClear && Boolean(value) ? (
                <IconButton
                    style={{width: 24, height: 24}}
                    onClick={(e) => {
                        e.stopPropagation();
                        setValue(undefined);
                        setPlaceholder(undefined);
                        onClear && onClear();
                    }}
                >
                    <CrossIcon bgColor="transparent" color="#7E8C9E" scale={2}/>
                </IconButton>
            ) : (
                <ChevronIcon rotate={open ? "top" : undefined} color="#7E8C9E"/>
            )}
          </span>
                </Button>
                {actionSection}
            </div>

            <Menu
                id="basic-menu"
                sx={{direction: "rtl"}}
                PaperProps={{
                    style: {
                        width: btnRef.current && btnRef.current.offsetWidth,
                        maxHeight: 400,
                        ...(props.isNested && {
                            minWidth: 300,
                            overflowY: "scroll",
                            // position: "relative",
                        }),
                    },
                }}
                anchorOrigin={{vertical: "bottom", horizontal: "left"}}
                transformOrigin={{horizontal: "left", vertical: "top"}}
                anchorEl={anchorEl}
                open={open}
                onClose={() => setAnchorEl(null)}
                MenuListProps={{
                    "aria-labelledby": "basic-button",
                    style: {padding: 0},
                }}
                disableScrollLock
            >
                {withSearch && (
                    <div
                        className="bg-white border-slate-200 p-1 sticky top-0 z-10"
                        style={{borderBottom: "1px solid"}}
                    >
                        <BasicTextInput
                            TextFieldProps={{autoFocus: true, inputProps: {style: {fontSize: fontSizes[size]}}}}
                            placeHolder="جستجو"
                            onChange={(val) => {
                                let v = val.toString();
                                if (val === "") {
                                    setNested(props.isNested);
                                    setOptions(props.options);
                                } else {
                                    props.isNested && setNested(false);
                                    setOptions(search(v));
                                }
                            }}
                            style={{border: 0, height: hights[size], fontSize: fontSizes[size]}}
                        />
                    </div>
                )}
                <div>
                    {isNested ? (
                        <ListSubItem
                            data={options}
                            onChange={(item) => handleChange(item)}
                            sizes={{font: fontSizes[size]}}
                        />
                    ) : (
                        Array.isArray(options) &&
                        options.map((option, index) => {
                            const {image, label, dsc, ind, title} = option;

                            const fontSize = fontSizes[size];
                            return (
                                <>
                                    <MenuItem
                                        key={index}
                                        onClick={() => handleChange(option)}
                                        style={{...menuItemStyle}}
                                        className="py-3"
                                    >
                                        <div className="flex gap-4 items-center">
                                            {image && (
                                                <div
                                                    className="w-7 h-7 rounded-full overflow-hidden border
            border-slate-200 bg-center bg-cover bg-no-repeat"
                                                    style={{backgroundImage: `url("${image}")`}}
                                                />
                                            )}
                                            <div className="flex flex-col gap-1 justify-center">
                                                {ind && title ? (
                                                    <div className="flex gap-1 text-xs md:text-sm items-center">
                                                        <span style={{fontSize}}>{ind}</span>
                                                        <div>
                              <span
                                  className="bg-slate-100 px-1.5 rounded text-slate-400 font-bold"
                                  style={{fontSize}}
                              >
                                #
                              </span>
                                                            <span>{" - "}</span>
                                                        </div>
                                                        <span style={{fontSize}}>{title}</span>
                                                    </div>
                                                ) : (
                                                    <span style={{fontSize}}>{label}</span>
                                                )}
                                                <span className="text-xs text-slate-400">{dsc}</span>
                                            </div>
                                        </div>
                                    </MenuItem>
                                </>
                            );
                        })
                    )}
                </div>
                {footer && (
                    <div className="bg-white border-slate-200 p-2 sticky bottom-0" style={{borderTop: "1px solid"}}>
                        <div
                            onClick={() => {
                                if (props.closeOnFooterClick) setAnchorEl(null);
                            }}
                        >
                            {footer}
                        </div>
                    </div>
                )}
            </Menu>
        </div>
    );
}
